import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { defaultAxiosInstance } from '../api/defaultInstance';
import '../css/PasswordChange.css';

const PasswordChange = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // 입력 시 해당 필드의 에러 메시지 제거
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // 현재 비밀번호 검증
    if (!formData.currentPassword.trim()) {
      newErrors.currentPassword = '현재 비밀번호를 입력해주세요.';
    }

    // 새 비밀번호 검증
    if (!formData.newPassword.trim()) {
      newErrors.newPassword = '새 비밀번호를 입력해주세요.';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = '비밀번호는 최소 8자 이상이어야 합니다.';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.newPassword)) {
      newErrors.newPassword = '비밀번호는 영문 대소문자와 숫자를 포함해야 합니다.';
    }

    // 비밀번호 확인 검증
    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = '비밀번호 확인을 입력해주세요.';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = '비밀번호가 일치하지 않습니다.';
    }

    // 현재 비밀번호와 새 비밀번호가 같은지 검증
    if (formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = '현재 비밀번호와 다른 비밀번호를 입력해주세요.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setSuccessMessage('');

    try {
      const response = await defaultAxiosInstance.put('/admin/password', {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });

      if (response.status === 200) {
        setSuccessMessage('비밀번호가 성공적으로 변경되었습니다.');
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        
        // 3초 후 대시보드로 이동
        setTimeout(() => {
          navigate('/dashboard');
        }, 1000);
      }
    } catch (error) {
      console.error('비밀번호 변경 실패:', error);
      
      if (error.response) {
        const { status, data } = error.response;
        
        switch (status) {
          case 401:
            setErrors({ currentPassword: '현재 비밀번호가 올바르지 않습니다.' });
            break;
          case 400:
            if (data.message) {
              setErrors({ general: data.message });
            } else {
              setErrors({ general: '잘못된 요청입니다.' });
            }
            break;
          case 500:
            setErrors({ general: '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.' });
            break;
          default:
            setErrors({ general: '비밀번호 변경에 실패했습니다. 다시 시도해주세요.' });
        }
      } else {
        setErrors({ general: '네트워크 오류가 발생했습니다. 인터넷 연결을 확인해주세요.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="password-change-container">
      <div className="password-change-card">
        <h2 className="password-change-title">비밀번호 변경</h2>
        
        {successMessage && (
          <div className="alert alert-success" role="alert">
            {successMessage}
          </div>
        )}

        {errors.general && (
          <div className="alert alert-danger" role="alert">
            {errors.general}
          </div>
        )}

        <form onSubmit={handleSubmit} className="password-change-form">
          <div className="form-group">
            <label htmlFor="currentPassword" className="form-label">
              현재 비밀번호
            </label>
            <input
              type="password"
              id="currentPassword"
              name="currentPassword"
              value={formData.currentPassword}
              onChange={handleInputChange}
              className={`form-control ${errors.currentPassword ? 'is-invalid' : ''}`}
              placeholder="현재 비밀번호를 입력하세요"
              disabled={isLoading}
            />
            {errors.currentPassword && (
              <div className="invalid-feedback">
                {errors.currentPassword}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="newPassword" className="form-label">
              새 비밀번호
            </label>
            <input
              type="password"
              id="newPassword"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleInputChange}
              className={`form-control ${errors.newPassword ? 'is-invalid' : ''}`}
              placeholder="새 비밀번호를 입력하세요 (최소 8자)"
              disabled={isLoading}
            />
            {errors.newPassword && (
              <div className="invalid-feedback">
                {errors.newPassword}
              </div>
            )}
            <small className="form-text text-muted">
              영문 대소문자, 숫자를 포함하여 최소 8자 이상 입력해주세요.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              새 비밀번호 확인
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
              placeholder="새 비밀번호를 다시 입력하세요"
              disabled={isLoading}
            />
            {errors.confirmPassword && (
              <div className="invalid-feedback">
                {errors.confirmPassword}
              </div>
            )}
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="btn btn-secondary"
              disabled={isLoading}
            >
              취소
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading ? '변경 중...' : '비밀번호 변경'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PasswordChange; 